import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { useCommands, useServices } from "../app/services";
import { isInFlight, type CommandPhase } from "../state/commands";
import { useStore } from "../state/store";
import { Icon } from "./Icon";

const PHASE: Record<CommandPhase, string> = {
  sending: "送信中", accepted: "受付済み・実行待ち", succeeded: "完了",
  rejected: "実行されませんでした", failed: "失敗", interrupted: "中断",
  send_failed: "送信できませんでした", unknown: "結果を確認できません",
};

/** 全画面共通。読み上げは各 store の announce に一本化する。 */
export function ToastViewport() {
  const cmds = useCommands();
  const { commands, toasts } = useServices();
  const notices = useStore(toasts.store);
  const location = useLocation();
  const navigate = useNavigate();
  const handled = useRef(new Set<string>());

  useEffect(() => {
    for (const c of cmds) {
      if (c.kind !== "capture" || c.phase !== "succeeded" || !c.resultId || handled.current.has(c.localId)) continue;
      handled.current.add(c.localId);
      // 待っている間に別の画面へ進んだ人の操作を奪わない。
      if (c.originKey === location.key) {
        void navigate(`/moments/${c.resultId}`);
        break;
      }
    }
  }, [cmds, location.key, navigate]);

  if (!cmds.length && !notices.length) return null;
  return (
    <section className="toast-viewport" aria-label="操作のお知らせ" tabIndex={-1}>
      {notices.map((n) => (
        <Toast key={`notice-${n.id}`} title="完了" message={n.message} success autoClose onClose={() => toasts.dismiss(n.id)} />
      ))}
      {cmds.map((c) => {
        const pending = isInFlight(c);
        const editor = c.kind === "capture" && c.phase === "succeeded" && c.resultId ? `/moments/${c.resultId}` : null;
        return (
          <Toast key={c.localId} title={PHASE[c.phase]} message={c.message}
            pending={pending} success={c.phase === "succeeded"}
            autoClose={c.phase === "succeeded" && (!editor || location.pathname === editor)}
            onClose={pending ? undefined : () => commands.dismiss(c.localId)}>
            {editor && location.pathname !== editor && <Link to={editor}>ここ好きを編集する</Link>}
          </Toast>
        );
      })}
    </section>
  );
}

function Toast({ title, message, success = false, pending = false, autoClose = false, onClose, children }: {
  title: string; message: string; success?: boolean; pending?: boolean; autoClose?: boolean;
  onClose?: () => void; children?: ReactNode;
}) {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    if (!autoClose || hovered || focused) return;
    const timer = setTimeout(() => close.current?.(), 6000);
    return () => clearTimeout(timer);
  }, [autoClose, hovered, focused, message]);
  return (
    <div className={`toast toast--${pending ? "pending" : success ? "success" : "error"}`}
      onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      onFocusCapture={() => setFocused(true)} onBlurCapture={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setFocused(false);
      }}>
      <Icon name={pending ? "clock" : success ? "check" : "warn"} className="toast__icon" />
      <div className="toast__body"><strong>{title}</strong><p>{message}</p>{children}</div>
      {onClose && <button type="button" className="icon-btn icon-btn--small" aria-label={`${message}のお知らせを閉じる`} onClick={onClose}>
        <Icon name="close" />
      </button>}
    </div>
  );
}
